// @djsem/render-state — Pro Integration dry-run adapters (Prompt 12F).
//
// DRY-RUN ONLY adapter boundary: maps internal DJSEM events (output frames, themed events,
// lighting events) into mock "would-send" payloads for external tools. It sends NOTHING —
// no OSC/MIDI/WebSocket/Art-Net/sACN/DMX/etc. (hard rule; rules 8, 10). Adapters consume
// meaning, never own it. Every produced event is `dryRunOnly: true`.

import {
  vArray, vBoolean, vObject, vString, type Validator,
  type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult,
} from "@djsem/shared";
import type { DJSEMOutputFrame } from "./outputFrame";
import { SeqCounter } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** Recognized dry-run/mock adapter kinds. Real hardware/protocol kinds are never accepted. */
export type DJSEMOutputAdapterKind =
  | "osc_dry_run" | "websocket_dry_run" | "midi_dry_run"
  | "touchdesigner_mock" | "resolume_mock" | "madmapper_mock";

/** All recognized {@link DJSEMOutputAdapterKind} values. */
export const DJSEM_OUTPUT_ADAPTER_KINDS: DJSEMOutputAdapterKind[] = [
  "osc_dry_run", "websocket_dry_run", "midi_dry_run", "touchdesigner_mock", "resolume_mock", "madmapper_mock",
];
// Real hardware/protocol adapter kinds that must NEVER be accepted here.
const FORBIDDEN_ADAPTER_KIND = /(artnet|art-net|sacn|e131|dmx|ilda|pangolin|fb4|timecode|real_|hardware)/i;

/** A single source-to-target field mapping within an adapter profile. */
export interface DJSEMOutputAdapterMapping {
  id: string;
  sourceEventType: "semantic_event" | "themed_event" | "output_frame" | "lighting_adapter_event";
  sourcePath: string;
  targetPath: string;
  transform?: string;
  enabled: boolean;
}

/** A configured dry-run output adapter: its kind, mappings, and required plan. */
export interface DJSEMOutputAdapterProfile {
  id: string;
  label: string;
  adapterKind: DJSEMOutputAdapterKind;
  enabled: boolean;
  dryRunOnly: true;
  requiredPlan: string;
  mappings: DJSEMOutputAdapterMapping[];
  warnings: string[];
}

/** One dry-run mapped event: a mock "would-send" payload, never actually transmitted. */
export interface DJSEMOutputAdapterDryRunEvent {
  eventId: string;
  adapterProfileId: string;
  createdAt: string;
  dryRunOnly: true;
  sourceEvent: unknown;
  mappedOutput: unknown;
  warnings: string[];
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link ProIntegrationDryRunAdapterService.mapOutputFrame}. */
export interface MapOutputFrameInput { profile: DJSEMOutputAdapterProfile; outputFrame: DJSEMOutputFrame; createdAt?: string; planLevel?: string; }
/** Input for {@link ProIntegrationDryRunAdapterService.mapThemedEvent}. */
export interface MapThemedEventInput { profile: DJSEMOutputAdapterProfile; themedEvent: Record<string, unknown>; createdAt?: string; planLevel?: string; }
/** Input for {@link ProIntegrationDryRunAdapterService.mapLightingEvent}. */
export interface MapLightingEventInput { profile: DJSEMOutputAdapterProfile; lightingEvent: Record<string, unknown>; createdAt?: string; planLevel?: string; }

const AT = "1970-01-01T00:00:00.000Z";
const PLAN_RANK: Record<string, number> = { free_starter: 0, creator: 1, creator_pro: 2, performer: 3, pro_integrations: 4, venue: 5, enterprise: 6 };

// ---- service (class-based; hard rule 12) ------------------------------------

const _seq = new SeqCounter();
function getPath(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, k) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[k] : undefined), obj);
}

/**
 * Dry-run-only adapter boundary: maps internal DJSEM events (output frames, themed events,
 * lighting events) into mock "would-send" payloads for external tools. It sends nothing —
 * no OSC/MIDI/WebSocket/Art-Net/sACN/DMX/etc. Every produced event is `dryRunOnly: true`.
 */
export class ProIntegrationDryRunAdapterService {
  /**
   * Validates an adapter profile: required fields, `dryRunOnly: true`, a supported (never
   * forbidden hardware) adapter kind, and well-formed mappings.
   * @param profile - the adapter profile to validate.
   * @returns a validation result listing any issues.
   */
  validateProfile(profile: DJSEMOutputAdapterProfile): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!profile?.id || !profile?.label) issues.push({ path: "$", message: "id and label required" });
    if (profile?.dryRunOnly !== true) issues.push({ path: "$.dryRunOnly", message: "adapters must be dryRunOnly: true" });
    if (!DJSEM_OUTPUT_ADAPTER_KINDS.includes(profile?.adapterKind)) issues.push({ path: "$.adapterKind", message: `unsupported adapter kind: ${String(profile?.adapterKind)}` });
    if (FORBIDDEN_ADAPTER_KIND.test(String(profile?.adapterKind))) issues.push({ path: "$.adapterKind", message: "real hardware/protocol adapters are not allowed" });
    for (const m of profile?.mappings ?? []) {
      if (!m.targetPath || !/^[\w./:-]+$/.test(m.targetPath)) issues.push({ path: `$.mappings.${m.id}`, message: `invalid target path: ${String(m.targetPath)}` });
      if (!m.sourcePath) issues.push({ path: `$.mappings.${m.id}`, message: "sourcePath required" });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "adapter_profile");
  }

  /**
   * Maps an output frame through every enabled output-frame mapping on the profile.
   * @param input - the profile, the output frame to map, and optional timestamp/plan level.
   * @returns one dry-run event per matching enabled mapping.
   */
  mapOutputFrame(input: MapOutputFrameInput): DJSEMOutputAdapterDryRunEvent[] {
    const warnings = this.planWarnings(input.profile, input.planLevel);
    return input.profile.mappings.filter((m) => m.enabled && m.sourceEventType === "output_frame").map((m) => this.mapOne(input.profile, input.outputFrame, m, input.createdAt, warnings));
  }
  /**
   * Maps a themed event through the profile's first enabled themed-event mapping (or a
   * passthrough mapping if none is configured).
   * @param input - the profile, the themed event to map, and optional timestamp/plan level.
   * @returns the resulting dry-run event.
   */
  mapThemedEvent(input: MapThemedEventInput): DJSEMOutputAdapterDryRunEvent {
    const warnings = this.planWarnings(input.profile, input.planLevel);
    const m = input.profile.mappings.find((x) => x.enabled && x.sourceEventType === "themed_event") ?? this.passthroughMapping();
    return this.mapOne(input.profile, input.themedEvent, m, input.createdAt, warnings);
  }
  /**
   * Maps a lighting adapter event through the profile's first enabled lighting-event
   * mapping (or a passthrough mapping if none is configured).
   * @param input - the profile, the lighting event to map, and optional timestamp/plan level.
   * @returns the resulting dry-run event.
   */
  mapLightingEvent(input: MapLightingEventInput): DJSEMOutputAdapterDryRunEvent {
    const warnings = this.planWarnings(input.profile, input.planLevel);
    const m = input.profile.mappings.find((x) => x.enabled && x.sourceEventType === "lighting_adapter_event") ?? this.passthroughMapping();
    return this.mapOne(input.profile, input.lightingEvent, m, input.createdAt, warnings);
  }

  /** @returns one default (disabled) adapter profile per supported adapter kind. */
  createDefaultProfiles(): DJSEMOutputAdapterProfile[] {
    const mk = (id: string, label: string, kind: DJSEMOutputAdapterKind, src: DJSEMOutputAdapterMapping["sourceEventType"]): DJSEMOutputAdapterProfile => ({
      id, label, adapterKind: kind, enabled: false, dryRunOnly: true, requiredPlan: "pro_integrations",
      mappings: [{ id: `${id}_m0`, sourceEventType: src, sourcePath: "intensityBudget.total", targetPath: `${kind}/intensity`, enabled: true }],
      warnings: ["Dry-run only. This adapter never sends real output."],
    });
    return [
      mk("osc_dry", "OSC (dry-run)", "osc_dry_run", "output_frame"),
      mk("ws_dry", "WebSocket (dry-run)", "websocket_dry_run", "output_frame"),
      mk("midi_dry", "MIDI (dry-run)", "midi_dry_run", "output_frame"),
      mk("td_mock", "TouchDesigner (mock)", "touchdesigner_mock", "output_frame"),
      mk("resolume_mock", "Resolume (mock)", "resolume_mock", "themed_event"),
      mk("madmapper_mock", "MadMapper (mock)", "madmapper_mock", "output_frame"),
    ];
  }

  // ---- private --------------------------------------------------------------
  private mapOne(profile: DJSEMOutputAdapterProfile, source: unknown, m: DJSEMOutputAdapterMapping, createdAt: string | undefined, warnings: string[]): DJSEMOutputAdapterDryRunEvent {
    const value = getPath(source, m.sourcePath);
    return {
      eventId: `dry_${profile.id}_${_seq.next()}`, adapterProfileId: profile.id, createdAt: createdAt ?? AT,
      dryRunOnly: true, sourceEvent: source,
      mappedOutput: { targetPath: m.targetPath, value, transform: m.transform, note: "dry-run: no real output sent" },
      warnings,
    };
  }
  private passthroughMapping(): DJSEMOutputAdapterMapping { return { id: "passthrough", sourceEventType: "themed_event", sourcePath: "", targetPath: "mock/passthrough", enabled: true }; }
  private planWarnings(profile: DJSEMOutputAdapterProfile, planLevel?: string): string[] {
    if (!planLevel) return [];
    if ((PLAN_RANK[planLevel] ?? 0) < (PLAN_RANK[profile.requiredPlan] ?? 99)) return [`Plan ${planLevel} is below required ${profile.requiredPlan}; dry-run allowed for preview only.`];
    return [];
  }
}
